import { useMemo } from 'react';
import type { VoidFunctionComponent } from 'react';
import type { Column } from 'react-table';

import { getSubscriptionPrice } from '../../../../shared/subscriptions/utils';
import SelectableReactTable from '../../generic/SelectableReactTable';
import type { InternalToolsOrg } from '../../../../shared/InternalTools/orgs';

type FilteredOrgTableProps = {
  isSearchOnly: boolean;
  orgs: InternalToolsOrg[];
  isLoading: boolean;
  selectedOrg: InternalToolsOrg | null;
  onSelectOrg: (org: InternalToolsOrg) => void;
};

const FilteredOrgTable: VoidFunctionComponent<FilteredOrgTableProps> = ({
  isSearchOnly,
  orgs,
  isLoading,
  selectedOrg,
  onSelectOrg,
}) => {
  const columns: Array<Partial<Column>> = useMemo(() => {
    let cols: Array<Partial<Column>> = [
      {
        Header: 'Title',
        accessor: 'title',
      },
      {
        Header: 'Org ID',
        accessor: 'id',
      },
    ];

    if (!isSearchOnly) {
      cols = cols.concat([
        {
          Header: 'Access Level',
          accessor: 'access_level',
        },
        {
          Header: 'Stripe Plan (per dev * month)',
          accessor: 'stripePlan',
        },
        {
          Header: 'Contributor Count',
          accessor: 'contributor_count',
        },
        {
          Header: 'Project Count',
          accessor: 'projectCount',
        },
        {
          Header: 'Custom Billing',
          accessor: 'customBilling',
        },
      ]);
    }
    return cols;
  }, [isSearchOnly]);

  const tableData = useMemo(() => {
    return orgs.map((org) => {
      const stripePlan = `$${getSubscriptionPrice(org.stripe_plan)}`;

      const customBilling = org.custom_billing ? 'yes' : 'no';

      return { ...org, customBilling, stripePlan };
    });
  }, [orgs]);

  return (
    // @ts-expect-error There are so many props in the table type that I don't think it's reasoable to fulfill them all.
    <SelectableReactTable
      // convert stripe plan values to be something that's human readble
      data={tableData}
      loading={isLoading}
      loadingText="fetching org data"
      showPagination
      showPaginationTop
      showPaginationBottom={false}
      defaultPageSize={10}
      className="-striped -highlight"
      columns={columns}
      selectedData={selectedOrg}
      onSelect={onSelectOrg}
      areEqual={(orgA, orgB) => orgA.id === orgB.id}
      selectedBackgroundColor="#00afec"
      selectedTextColor="black"
    />
  );
};

export default FilteredOrgTable;
